import React, { useState, useEffect } from 'react'
import Paginate from '../Paginate';
import Loader from '../Loader';
import axios from 'axios';
import { getNotification } from '../../Config/config';
import { useContext } from 'react';
import { UserContext } from '../../Contexts/UserContext';
import Constants from '../../Constants';
import { useMemo } from 'react';
import SortAscendingIcon from 'mdi-react/SortAscendingIcon';
import SortDescendingIcon from 'mdi-react/SortDescendingIcon';
import SortIcon from 'mdi-react/SortIcon';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import RandomLogo from '../RandomLogo';
import CompanyLogo from '../CompanyLogo';
import OverflowToolTip from '../shared/OverflowToolTip';
import { Link } from 'react-router-dom';
import { routeNames } from '../../routeSegments';
import {AiFillEye} from "react-icons/ai"
import {TbFileDownload} from "react-icons/tb"
import { Tooltip } from 'antd';

/**
 * Expandable message cell with View More / View Less toggle based on actual overflow
 */
const ExpandableMessage = ({ text }) => {
    const [expanded, setExpanded] = React.useState(false);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const previewRef = React.useRef(null);

    React.useEffect(() => {
        if (previewRef.current && !expanded) {
            // Check if text is overflowing
            const isOverflow = previewRef.current.scrollWidth > previewRef.current.clientWidth;
            setIsOverflowing(isOverflow);
        }
    }, [text, expanded]);

    if (!text) return "--";

    return (
        <div>
            {!expanded ? (
                <div className="flex items-center gap-1">
                    <div 
                        ref={previewRef}
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1
                        }}
                    >
                        {renderMessageWithLinks(text)}
                    </div>
                    {isOverflowing && (
                        <button 
                            onClick={() => setExpanded(true)} 
                            className="text-[#BC8B20] text-[14px] hover:underline focus:outline-none whitespace-nowrap flex-shrink-0"
                        >
                            view more
                        </button>
                    )}
                </div>
            ) : (
                <div>
                    <div style={{ wordBreak: 'break-word', whiteSpace: 'normal', marginBottom: '4px' }}>
                        {renderMessageWithLinks(text)}
                    </div>
                    <button 
                        onClick={() => setExpanded(false)} 
                        className="text-[#BC8B20] text-[14px] hover:underline focus:outline-none"
                    >
                        view less
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * Helper function to convert URLs in text to clickable links
 */
const renderMessageWithLinks = (text) => {
    if (!text) return text;
    
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
                <a
                    key={index}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#BC8B20] hover:underline cursor-pointer"
                >
                    {part}
                </a>
            );
        }
        return <span key={index}>{part}</span>;
    });
};

/**
 * Format date - only show date without time
 */
const formatDateTo12Hour = (dateString) => {
    if (!dateString) return "--";
    const [date] = dateString.split(", ");
    return date;
};

/**
 * Truncated header with tooltip on hover
 */
const TruncatedHeaderWithTooltip = ({ text }) => {
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const headerRef = React.useRef(null);

    React.useEffect(() => {
        if (headerRef.current) {
            const isOverflow = headerRef.current.scrollWidth > headerRef.current.clientWidth;
            setIsOverflowing(isOverflow);
        }
    }, [text]);

    if (!text) return "--";

    const truncatedElement = (
        <div 
            ref={headerRef}
            style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}
        >
            {text}
        </div>
    );

    if (isOverflowing) {
        return (
            <Tooltip title={text} placement="top">
                {truncatedElement}
            </Tooltip>
        );
    }

    return truncatedElement;
};

/**
 * Notifications component that displays a table of notifications
 * Handles alert, general, and request notification types
 * 
 * @param {string} notificationType - Type of notification (alert, general, request)
 * @param {boolean} isRead - Whether to show read or unread notifications
 */
function Notifications({ notificationType, isRead }) {

    const { getToken, logout } = useContext(UserContext);
    const [notifications, setNotifications] = useState(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        setIsLoading(true)
        axios(`${getNotification}`, {
            method: "POST",
            data: {},
            headers: {
                "Content-Type": "application/json",
                Authorization: getToken(),
            },
        })
            .then((response) => {
                setIsLoading(false)
                console.log(response, "Notification Response");
                setNotifications(response.data)
            })
            .catch((error) => {
                setIsLoading(false)
                if (error?.response?.status === Constants.sessionTerminatedStatus) {
                    logout();
                }
                console.log("API not working", error);
            });
    }, [getToken, logout]);

    /**
     * Generate columns based on notification type
     */
    const columns = useMemo(() => [
        {
            Header: "Date",
            accessor: "date",
            className: "w-[10%]",
            Cell: ({ value }) => {
                return formatDateTo12Hour(value);
            }
        },
        {
            Header: "Header",
            accessor: "header",
            className: "w-[23%]",
            Cell: ({ value }) => {
                return <TruncatedHeaderWithTooltip text={value} />
            }
        },
        {
            Header: "Message",
            accessor: "message",
            className: "w-[67%]",
            Cell: ({ value }) => {
                return <ExpandableMessage text={value} />
            }
        },
        ...notificationType === "alert" || notificationType === "request" 
            ? [{
                Header: "Notification Type",
                accessor: "type",
                Cell: ({ value }) => {
                    return value || "--";
                }
            }]
            : []
        // Action column hidden
        // {
        //     Header: "Action",
        //     accessor: "action",
        //     disableSortBy: true,
        //     Cell: ({ value }) => {
        //         if (notificationType === "general") {
        //             return (
        //                 <Tooltip placement='topLeft' title={`Download ${value?.tooltip || ''}`}>
        //                     <Link to={value?.url || "#"}>
        //                         <TbFileDownload color='#BC8B20' size={22} />
        //                     </Link>
        //                 </Tooltip>
        //             );
        //         }
        //         return (
        //             <Link to={value?.url || "#"}>
        //                 <AiFillEye color='#BC8B20' size={22} />
        //             </Link>
        //         );
        //     }
        // }
    ], [notificationType]);

    /**
     * Prepare data for the table based on notification type and read status
     */
    const data = useMemo(() => {
        let row = [];
        
        // Handle different notification type structures
        if (notificationType === "alert") {
            // Get the appropriate notifications based on read_at field
            const notificationList = notifications?.alert?.read_and_unread || [];
            const filteredNotifications = notificationList.filter(notification => {
                if (!isRead) {
                    return notification?.read_at !== null && notification?.read_at !== undefined;
                } else {
                    return !notification?.read_at;
                }
            });
            
            filteredNotifications.forEach(notification => {
                row.push({
                    company: { 
                        name: notification?.entity_name, 
                        id: notification?.entity_id, 
                        icon_path: notification?.entity_logo,
                        url: notification?.url
                    },
                    type: notification?.entity_type,
                    header: notification?.notification_type,
                    message: notification?.message,
                    date: notification?.notified_date || notification?.date,
                    action: { url: notification?.url }
                });
            });
        } else if (notificationType === "general") {
            // Get the appropriate notifications based on read_at field
            const notificationList = notifications?.general?.read_and_unread || [];
            const filteredNotifications = notificationList.filter(notification => {
                if (!isRead) {
                    return notification?.read_at !== null && notification?.read_at !== undefined;
                } else {
                    return !notification?.read_at;
                }
            });
            
            filteredNotifications.forEach(notification => {
                row.push({
                    company: { 
                        name: notification?.entity_name, 
                        id: notification?.entity_id, 
                        icon_path: notification?.entity_logo,
                        url: notification?.url
                    },
                    header: notification?.notification_type,
                    message: notification?.message,
                    date: notification?.notified_date || notification?.date,
                    action: { 
                        tooltip: notification?.file_name,
                        url: notification?.url
                    }
                });
            });
        } else if (notificationType === "request") {
            // Get the appropriate notifications based on read_at field
            const notificationList = notifications?.request?.read_and_unread || [];
            const filteredNotifications = notificationList.filter(notification => {
                if (!isRead) {
                    return notification?.read_at !== null && notification?.read_at !== undefined;
                } else {
                    return !notification?.read_at;
                }
            });
            
            filteredNotifications.forEach(notification => {
                row.push({
                    company: { 
                        name: notification?.entity_name, 
                        id: notification?.entity_id, 
                        icon_path: notification?.entity_logo,
                        url: notification?.url
                    },
                    type: notification?.entity_type,
                    header: notification?.notification_type,
                    message: notification?.message,
                    date: notification?.notified_date || notification?.date,
                    action: { url: notification?.url }
                });
            });
        }
        
        return row;
    }, [isRead, notificationType, notifications]);

    /**
     * Initialize react-table
     */
    const tableInstance = useTable(
        {
            columns,
            data,
            disableSortRemove: true,
            initialState: { pageSize: 10, pageIndex: 0 },
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        prepareRow,
        gotoPage,
        state: { pageIndex, pageSize }
    } = tableInstance;

    return (
        <>
            <div className='overflow-auto scrollbar'>
                <table className="w-full" style={{ tableLayout: 'fixed' }} {...getTableProps()}>
                    <thead className="text-[12pt] leading-[15pt] bg-[#EDEDED] lg:text-[12px] lg:leading-[15px] text-[#666666] font-sans_semi_bold py-4 px-0 text-center">
                        {headerGroups.map(headerGroup => (
                            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th key={column.id} {...column.getHeaderProps([
                                        {
                                            className: `py-3.5 px-5 whitespace-nowrap ${column.className ?? ''}`,
                                            style: column.headerStyle,
                                        },
                                        column.getSortByToggleProps(),
                                    ])}>                                    
                                        <div className="flex">
                                            <span className="mr-1">{column.render('Header')}</span>
                                            {column.disableSortBy
                                                ? null
                                                : column.isSorted
                                                    ? column.isSortedDesc
                                                        ? <SortAscendingIcon size={16} className="mt-[0.15rem] lg:mt-0" />
                                                        : <SortDescendingIcon size={16} className="mt-[0.15rem] lg:mt-0" />
                                                    : <SortIcon size={16} className="mt-[0.15rem] lg:mt-0" />
                                            }
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody {...getTableBodyProps()} className="text-[12pt] leading-[16pt] lg:text-[14px] font-sans_book_body text-left">
                        {page.map(row => {
                            prepareRow(row);
                            return (
                                <tr key={row.id} {...row.getRowProps()} className="border-b">
                                    {row.cells.map(cell => (
                                        <td key={cell.id} {...cell.getCellProps([
                                            {
                                                className: `${cell.column.id === 'message' ? '' : 'whitespace-nowrap'} px-5 py-4 ${cell.column.className ?? ''}`,
                                                style: cell.column.style,
                                            },
                                        ])}>
                                            {cell.value ? cell.render('Cell') : "--"}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                
                {page?.length === 0 && (
                    <div className="w-full h-40 flex items-center justify-center">
                        {isLoading ? <Loader /> : "No data found"}
                    </div>
                )}
            </div>
            
            {rows?.length > 5 && (
                <Paginate
                    currentPage={pageIndex + 1}
                    totalCount={rows?.length}
                    pageSize={pageSize}
                    onPageChange={(page) => {
                        gotoPage(page - 1);
                    }}
                />
            )}
        </>
    );
}

export default Notifications;